import urljoin from 'url-join';

import { resolveHostUsingEnvironmentInfo } from './ResolveHost';

/**
 * Service path types for Orchestrator endpoints
 */
export enum OrchestratorServicePath {
    /** Standard Orchestrator OData/API endpoints */
    Orchestrator = 'orchestrator_',
    /** Resource Catalog service (Tags) */
    ResourceCatalog = 'resourcecatalog_',
    /** OData endpoint for Tasks (no orchestrator_ prefix) */
    OData = 'odata',
}

/**
 * Returns the base route for direct Orchestrator API calls.
 *
 * This is similar to getIdentityBaseRoute but for Orchestrator services.
 *
 * @param accountLogicalName - The organization/account logical name
 * @param tenantName - The tenant name
 * @param servicePath - The service path type (orchestrator_, resourcecatalog_, or odata)
 * @returns The full base URL for Orchestrator API calls
 *
 * @example
 * // Returns: /myorg/mytenant/orchestrator_ (on localhost, uses proxy)
 * // Returns: https://cloud.uipath.com/myorg/mytenant/orchestrator_ (in production)
 * getOrchestratorBaseRoute('myorg', 'mytenant', OrchestratorServicePath.Orchestrator)
 *
 * @example
 * // Returns: /myorg/mytenant/resourcecatalog_ (on localhost, uses proxy)
 * // Returns: https://cloud.uipath.com/myorg/mytenant/resourcecatalog_ (in production)
 * getOrchestratorBaseRoute('myorg', 'mytenant', OrchestratorServicePath.ResourceCatalog)
 */
export const getOrchestratorBaseRoute = (
    accountLogicalName: string,
    tenantName: string,
    servicePath: OrchestratorServicePath = OrchestratorServicePath.Orchestrator
): string => {
    // On localhost, return relative URL to use the dev server proxy
    if (window.location.origin.includes('localhost')) {
        return urljoin('/', accountLogicalName, tenantName, servicePath);
    }

    const resolvedHost = resolveHostUsingEnvironmentInfo(window.location.host);
    return urljoin(`https://${resolvedHost}`, accountLogicalName, tenantName, servicePath);
};

/**
 * Returns the base route for direct Orchestrator OData API calls.
 * Convenience wrapper that uses OrchestratorServicePath.Orchestrator and appends /odata.
 *
 * @param accountLogicalName - The organization/account logical name
 * @param tenantName - The tenant name
 * @returns The full base URL for Orchestrator OData API calls
 *
 * @example
 * // Returns: https://alpha.uipath.com/myorg/mytenant/orchestrator_/odata
 * getOrchestratorODataRoute('myorg', 'mytenant')
 */
export const getOrchestratorODataRoute = (
    accountLogicalName: string,
    tenantName: string
): string => urljoin(
    getOrchestratorBaseRoute(accountLogicalName, tenantName, OrchestratorServicePath.Orchestrator),
    'odata'
);

/**
 * Returns the base route for direct Orchestrator API (non-OData) calls.
 *
 * @param accountLogicalName - The organization/account logical name
 * @param tenantName - The tenant name
 * @returns The full base URL for Orchestrator API calls
 *
 * @example
 * // Returns: https://alpha.uipath.com/myorg/mytenant/orchestrator_/api
 * getOrchestratorApiRoute('myorg', 'mytenant')
 */
export const getOrchestratorApiRoute = (
    accountLogicalName: string,
    tenantName: string
): string => urljoin(
    getOrchestratorBaseRoute(accountLogicalName, tenantName, OrchestratorServicePath.Orchestrator),
    'api'
);

/**
 * Returns the base route for direct Resource Catalog (Tags) API calls.
 *
 * @param accountLogicalName - The organization/account logical name
 * @param tenantName - The tenant name
 * @returns The full base URL for Resource Catalog API calls
 *
 * @example
 * // Returns: https://alpha.uipath.com/myorg/mytenant/resourcecatalog_
 * getResourceCatalogRoute('myorg', 'mytenant')
 */
export const getResourceCatalogRoute = (
    accountLogicalName: string,
    tenantName: string
): string => getOrchestratorBaseRoute(accountLogicalName, tenantName, OrchestratorServicePath.ResourceCatalog);

/**
 * Returns the base route for direct Orchestrator Tasks API calls.
 * Tasks use a different URL pattern without the orchestrator_ prefix.
 *
 * @param accountLogicalName - The organization/account logical name
 * @param tenantName - The tenant name
 * @returns The full base URL for Tasks OData API calls
 *
 * @example
 * // Returns: https://alpha.uipath.com/myorg/mytenant/odata
 * getOrchestratorTasksRoute('myorg', 'mytenant')
 */
export const getOrchestratorTasksRoute = (
    accountLogicalName: string,
    tenantName: string
): string => getOrchestratorBaseRoute(accountLogicalName, tenantName, OrchestratorServicePath.OData);
